import PageLayout from "../../components/PageLayout";
import Table from "../../components/ReusableComponents/Table";
import {
    greyfleetMenuGroups,
    initGreyFleetTableContents,
} from "../../components/AdminGreyFleetPage/GreyFleetTableInit";
import CollapsibleSection from "../../components/ReusableComponents/CollapsibleSection";
import GreyFleetVehicleAnalysisCharts from "../../components/ChartGroups/GreyFleetVehicleAnalysisCharts";
import GreyFleetDriversSection from "../../components/AdminGreyFleetPage/GreyFleetDriversSection";
import PaginationNav from "../../components/PaginationNav";
import useAdminGreyFleet from "./pageHooks/useAdminGreyFleet";
import GreyFleetPdfReport from "../../components/AdminGreyFleetPage/GreyFleetPdfReport";
import HeaderOptions from "../../components/ReusableComponents/HeaderOptions";
import DriversListFilters from "../../components/DriverListFilters/DriversListFilters";
import { greyFleetMenuItems } from "../../components/AdminGreyFleetPage/greyFleetTableMenuOptions";
import { type GreyFleetTableData } from "../../models/api/admin/greyFleet";
import greyFleetAnalysisContent from "../../components/GreyFleetAnalysis/greyFleetAnalysisContent";
import useLocalStorage from "../../hooks/useLocalStorage";
import { useRef } from "react";
import { useScrollToTable } from "../../hooks/useScrollToTable";
import { useGreyFleetTableActions } from "../../components/AdminGreyFleetPage/useGreyFleetTableActions";
import VehicleSelectDialog from "../../components/AdminGreyFleetPage/VehicleSelectDialog";
import useAuth from "../../auth";
import { isS2dUser as isS2dUserHelper } from "../../auth/userAccessHelpers";
import OldAnalysisComponent from "../../components/ReusableComponents/AnalysisComponent/OldAnalysisComponent";
import { GreyFleetReportTypes } from "../../models/api/reporting";
import { useCompany } from "../../api/admin/company";
import { tableHeaderOptionsEnum } from "../../hooks/useTableHeaders";
import useDriversListPaginationState from "../../hooks/useDriversListPaginationState";
import { driverListOrderByOptions } from "../../components/DriverList/DriverListTableOptions";

export default function AdminGreyFleet() {
    const {
        greyFleetAlertsData,
        isLoadingGreyFleetAlertsData,
        greyFleetAlertsError,
        greyFleetDriversList,
        isGettingGreyFleetDriversList,
        setPageNumber,
        pageSize,
        setPageSize,
        tableHeaders,
        setTableHeaders,
        hiddenCols,
        setHiddenCols,
        isVehicleAnalysisOpen,
        setIsVehicleAnalysisOpen,
        isGreyFleetDriversOpen,
        setIsGreyFleetDriversOpen,
        employmentFilters,
        showReportView,
        handleDownloadPdfReport,
        reportRef,
        handleFetchDrivers,
        filterModalOpen,
        setFilterModalOpen,
        query,
        setQuery,
        handleDownloadReport,
        isReportDownloading,
    } = useAdminGreyFleet();

    const {
        handleTableMenuSelect,
        isLoadingTableMenu,
        isVerifyInsuranceDialogOpen,
        setVerifyInsuranceDialogOpen,
        isRemoveVehicleDialogOpen,
        setRemoveVehicleDialogOpen,
        selectedDriverData,
        resetSelectedDriverData,
        handleVerifyInsurance,
        handleRemoveVehicle,
    } = useGreyFleetTableActions(handleFetchDrivers);

    const { decodedToken } = useAuth();
    const isS2dUser = isS2dUserHelper(decodedToken?.role);

    const tableRef = useRef<HTMLDivElement | null>(null);

    const analysisContent = greyFleetAnalysisContent(
        greyFleetAlertsData,
        greyFleetAlertsError
    );

    const [isGreyFleetAnalysisOpen, setIsGreyFleetAnalysisOpen] =
        useLocalStorage<boolean>("isGreyFleetAnalysisOpen", true);

    const hasLoaded = !!greyFleetDriversList && !isGettingGreyFleetDriversList;
    useScrollToTable(hasLoaded, tableRef);

    /** If selectedCompany exists
     * fetch company so we can get correct risk profiles for table.
     *
     * Delays loading time of table.
     */
    const { selectedCompany, selectedDepartment } = employmentFilters;
    const { companyData, isLoadingCompany } = useCompany(
        selectedCompany?.companyId
    );

    const tableContents = initGreyFleetTableContents(
        greyFleetDriversList?.items,
        companyData?.companyRiskProfiles
    );

    const paginationState = useDriversListPaginationState("greyfleet");

    const handleSortColumn = (newOrderBy?: string) => {
        if (!newOrderBy) return;
        if (paginationState.orderBy === newOrderBy) {
            if (paginationState.sortOrder === "asc") {
                paginationState.setSortOrder("desc");
            } else {
                paginationState.setSortOrder("asc");
            }
        }
        paginationState.setOrderBy(newOrderBy);
    };

    return (
        <>
            <PageLayout title="Grey Fleet Dashboard" header={false}>
                <div className="flex flex-col gap-6">
                    <HeaderOptions
                        employmentFilters={employmentFilters}
                        handleDownloadPdfReport={handleDownloadPdfReport}
                        handleDownloadCsvReport={() =>
                            handleDownloadReport(GreyFleetReportTypes.CSV)
                        }
                        handleDownloadXlsReport={() =>
                            handleDownloadReport(GreyFleetReportTypes.XLS)
                        }
                        isReportDownloading={
                            showReportView || isReportDownloading
                        }
                    />
                    <CollapsibleSection
                        title={"Grey Fleet analysis"}
                        isOpen={isGreyFleetAnalysisOpen}
                        setIsOpen={setIsGreyFleetAnalysisOpen}
                        content={
                            <OldAnalysisComponent
                                analysisContent={analysisContent}
                                isLoadingAnalysis={isLoadingGreyFleetAlertsData}
                            />
                        }
                    />
                    <CollapsibleSection
                        isOpen={isVehicleAnalysisOpen}
                        setIsOpen={setIsVehicleAnalysisOpen}
                        title={"Vehicle Analysis"}
                        content={
                            <GreyFleetVehicleAnalysisCharts
                                companyId={selectedCompany?.companyId}
                                departmentId={selectedDepartment?.departmentId}
                            />
                        }
                    />
                    <CollapsibleSection
                        isOpen={isGreyFleetDriversOpen}
                        setIsOpen={setIsGreyFleetDriversOpen}
                        title={"Your grey fleet drivers"}
                        content={
                            <GreyFleetDriversSection
                                driverCount={
                                    greyFleetAlertsData?.greyFleetDriverCount
                                }
                            />
                        }
                    />
                    <DriversListFilters
                        filterModalOpen={filterModalOpen}
                        setFilterModalOpen={setFilterModalOpen}
                        isFetchingDrivers={isGettingGreyFleetDriversList}
                        handleFetchDrivers={handleFetchDrivers}
                        query={query}
                        setQuery={setQuery}
                    />
                    <Table
                        tableHeaders={tableHeaders}
                        setTableHeaders={(value) =>
                            setTableHeaders(
                                value,
                                tableHeaderOptionsEnum.greyFleet
                            )
                        }
                        tableContents={tableContents}
                        menuItems={(data: GreyFleetTableData) =>
                            greyFleetMenuItems(
                                data,
                                handleTableMenuSelect,
                                isLoadingTableMenu,
                                isS2dUser
                            )
                        }
                        menuGroups={greyfleetMenuGroups}
                        hiddenCols={hiddenCols}
                        setHiddenCols={(value) =>
                            setHiddenCols(
                                value,
                                tableHeaderOptionsEnum.greyFleet
                            )
                        }
                        orderBy={paginationState.orderBy}
                        orderByOptions={driverListOrderByOptions}
                        sortOrder={paginationState.sortOrder as "asc" | "desc"}
                        handleSortColumn={handleSortColumn}
                        isLoading={
                            isGettingGreyFleetDriversList ||
                            (isLoadingCompany &&
                                selectedCompany !== undefined &&
                                selectedCompany !== null)
                        }
                        tableRef={tableRef}
                    />
                    {/*verify insurance*/}
                    <VehicleSelectDialog
                        isOpen={isVerifyInsuranceDialogOpen}
                        setIsOpen={setVerifyInsuranceDialogOpen}
                        title="Verify vehicle insurance"
                        selectedDriverData={selectedDriverData}
                        resetSelectedDriverData={resetSelectedDriverData}
                        onSubmit={handleVerifyInsurance}
                        isLoading={isLoadingTableMenu}
                        type="verify-insurance"
                    />
                    {/*remove vehicle*/}
                    <VehicleSelectDialog
                        isOpen={isRemoveVehicleDialogOpen}
                        setIsOpen={setRemoveVehicleDialogOpen}
                        title="Remove a vehicle"
                        selectedDriverData={selectedDriverData}
                        resetSelectedDriverData={resetSelectedDriverData}
                        onSubmit={handleRemoveVehicle}
                        isLoading={isLoadingTableMenu}
                        type="remove-vehicle"
                    />
                    {greyFleetDriversList && (
                        <>
                            {greyFleetDriversList?.items.length === 0 &&
                                !isGettingGreyFleetDriversList && (
                                    <div className="rounded border-2 border-S2D-error-40 bg-S2D-error-90 p-4 text-S2D-neutral-10">
                                        No drivers found
                                    </div>
                                )}
                            <PaginationNav
                                setPageNumber={setPageNumber}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                totalCount={greyFleetDriversList?.totalCount}
                                totalPages={greyFleetDriversList?.totalPages}
                                currentPage={greyFleetDriversList?.pageNumber}
                                hasPreviousPage={
                                    greyFleetDriversList?.hasPreviousPage
                                }
                                hasNextPage={greyFleetDriversList?.hasNextPage}
                            />
                        </>
                    )}
                </div>
            </PageLayout>
            {showReportView && (
                // NB report is never actually intended to be seen by the user, only appended to the bottom of the page
                <>
                    <div ref={reportRef}>
                        <GreyFleetPdfReport
                            analysisData={greyFleetAlertsData}
                            company={selectedCompany}
                            department={selectedDepartment}
                        />
                    </div>
                </>
            )}
        </>
    );
}
